import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import InputAdornment from "@mui/material/InputAdornment";
import LinearProgress from "@mui/material/LinearProgress";

import { useConfigContext } from "shared/contexts/ConfigContext";
import { useKeyBinding } from "shared/hooks";

import TextField from "features/components/TextField";
import { DEFAULT_FILTER_BUILDER_STATE } from "features/ui/Filters/FilterBuilder/constants";
import { filterBuilderQueryToFilterBuilderState } from "features/ui/Filters/FilterBuilder/utils";
import { useFilterSortState } from "features/ui/Filters/hooks";
import SearchIcon from "features/ui/Icons/SearchIcon";
import MatchingTextHighlight from "features/ui/MatchingTextHighlight";

import GlobalSearchControlsInfo, {
  GlobalSearchEnterIcon,
} from "./GlobalSearchControlsInfo";
import { useGlobalSearch } from "./hooks";
import { SearchResource } from "./types";
import { createLinkForResultItem } from "./utils";

interface SingleResourceListProps {
  items: any[];
  config: SearchResource;
  searchInput: string;
  firstResultIndex: number;
  selectedIndex: number;
  onClick: () => void;
}

interface ListItemProps {
  item: any;
  config: SearchResource;
  index: number;
  innerSelectedIndex?: number;
  searchInput: string;
  onClick: () => void;
}

const SearchResultItem = ({
  config,
  item,
  onClick,
  searchInput,
  index,
  innerSelectedIndex,
}: ListItemProps) => {
  const elementRef = useRef<HTMLAnchorElement>(null);
  const secondaryText = config.searchFields
    .filter((field) => field.fieldName !== config.nameField)
    .map((field) => item[field.fieldName])
    .filter(Boolean)
    .join(", ");

  if (index === innerSelectedIndex) {
    elementRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }

  return (
    <Link
      ref={elementRef}
      key={item[config.idField]}
      className={classNames(
        "p-2 border border-gray-200 flex flex-row items-center rounded-sm gap-4 hover:bg-gray-200",
        { "bg-gray-200": index === innerSelectedIndex }
      )}
      to={
        config.getItemRoute
          ? config.getItemRoute(item)
          : createLinkForResultItem(config, item[config.idField])
      }
      onClick={onClick}
      replace={true}
      reloadDocument={true}
      data-testid={`search-result-item-${index}`}
    >
      <div className="text-gray-400">{config.icon}</div>
      <MatchingTextHighlight
        text={item[config?.nameField]}
        secondarySearchTerms={[searchInput]}
        searchTerms={[]}
      />

      <MatchingTextHighlight
        text={secondaryText}
        secondarySearchTerms={[searchInput]}
        searchTerms={[]}
      />

      {index === innerSelectedIndex && (
        <div className="ml-auto mr-2">
          <GlobalSearchEnterIcon />
        </div>
      )}
    </Link>
  );
};

// First, create a type for the ref functions
interface SearchResultListRef {
  handleEnterPress: () => void;
}

// Modify SearchResultList to use forwardRef
const SearchResultList = forwardRef<
  SearchResultListRef,
  SingleResourceListProps
>(
  (
    { items, config, searchInput, firstResultIndex, selectedIndex, onClick },
    ref
  ) => {
    const { pages } = useConfigContext();

    const innerSelectedIndex =
      selectedIndex - firstResultIndex >= 0
        ? selectedIndex - firstResultIndex
        : undefined;

    const defaultVehicleFilters =
      filterBuilderQueryToFilterBuilderState(
        pages.claimAnalytics?.defaultVehicleFilters
      ) || DEFAULT_FILTER_BUILDER_STATE;

    const { updateFilters } = useFilterSortState({
      pageKey: config.resetFilterKey || "",
      disableUsingQuery: true,
      defaultFilterValues: defaultVehicleFilters,
      schemaAttributes: [],
    });

    const handleClick = () => {
      if (config.resetFilterKey) {
        updateFilters(defaultVehicleFilters);
      }

      onClick();
    };

    // Create the handler function
    const handleEnterPress = useCallback(() => {
      if (config.resetFilterKey) {
        updateFilters(defaultVehicleFilters);
      }

      onClick();
    }, [config.resetFilterKey, defaultVehicleFilters, onClick, updateFilters]);

    // Expose the handler function via ref
    useImperativeHandle(
      ref,
      () => ({
        handleEnterPress,
      }),
      [handleEnterPress]
    );

    if (items.length === 0) {
      return;
    }

    return (
      <ul
        key={config.key}
        className="flex flex-col gap-2"
        data-testid="search-result-group"
        data-items-length={items.length}
      >
        <div className="font-bold text-sm mt-2" data-testid="group-title">
          {config.type}
        </div>
        {items.map((item: any, index: number) => (
          <SearchResultItem
            key={item[config.idField]}
            config={config}
            item={item}
            index={index}
            innerSelectedIndex={innerSelectedIndex}
            searchInput={searchInput}
            onClick={handleClick}
          />
        ))}
      </ul>
    );
  }
);

interface GlobalSearchProps {
  onClick: () => void;
}

const GlobalSearch = ({ onClick }: GlobalSearchProps) => {
  const { t } = useTranslation("common");
  const {
    pages,
    general: { topHeader },
  } = useConfigContext();
  const [searchInput, setSearchInput] = useState("");
  const { results, loading, debouncedSearchInput } = useGlobalSearch({
    searchInput,
  });
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const navigate = useNavigate();

  const flatResults = results.flatMap(({ config, items }) =>
    items.map((item) => ({ config, item }))
  );

  const handleInputChange = (
    ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setSelectedIndex(0);
    setSearchInput(ev.target.value);
  };

  // Create refs for each SearchResultList
  const resultListRefs = useRef<{
    [key: string]: React.RefObject<SearchResultListRef | null>;
  }>({});

  const getResultListRef = (key: string) => {
    resultListRefs.current[key] ??= React.createRef<SearchResultListRef>();

    return resultListRefs.current[key];
  };

  const handleSelectedEnter = () => {
    const selected = flatResults[selectedIndex];
    if (!selected) {
      return;
    }

    const { config, item } = selected;

    const link = config.getItemRoute
      ? config.getItemRoute(item)
      : createLinkForResultItem(config, item[config.idField]);

    if (window.location.pathname === link.pathname) {
      // If we're on the same page, use location.href
      window.location.href = `${link.pathname}${link.search || ""}`;
    } else {
      // Otherwise use navigate
      navigate(link, {
        replace: true,
      });
    }

    resultListRefs.current[config.key]?.current?.handleEnterPress();
    onClick();
  };

  useKeyBinding(
    ["ArrowUp"],
    () => selectedIndex > 0 && setSelectedIndex(selectedIndex - 1)
  );
  useKeyBinding(
    ["ArrowDown"],
    () =>
      flatResults.length - 1 > selectedIndex &&
      setSelectedIndex(selectedIndex + 1)
  );
  useKeyBinding(["Enter"], () => handleSelectedEnter());

  const searchTextPlaceholder =
    topHeader?.searchTextPlaceholder ||
    pages.topHeader?.searchTextPlaceholder ||
    t("global_search.placeholder");

  return (
    <div className="min-h-24 flex flex-col">
      <TextField
        fullWidth
        autoFocus
        placeholder={searchTextPlaceholder}
        data-testid="global-search-input"
        onChange={handleInputChange}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          },
        }}
      />
      <div
        className="max-h-[32rem] overflow-auto mt-4"
        data-testid="search-results"
      >
        {debouncedSearchInput !== "" &&
          results.map(({ config, items, firstResultIndex }) => (
            <SearchResultList
              key={config.key}
              ref={getResultListRef(config.key)}
              selectedIndex={selectedIndex}
              firstResultIndex={firstResultIndex}
              items={items}
              config={config}
              searchInput={debouncedSearchInput}
              onClick={onClick}
            />
          ))}
        {debouncedSearchInput === "" && (
          <div className="flex flex-col justify-center text-sm h-5">
            {t("global_search.start_typing")}
          </div>
        )}
        {debouncedSearchInput !== "" &&
          !loading &&
          flatResults.length === 0 && (
            <div className="flex flex-col justify-center text-sm h-5">
              {t("common:no_results")}
            </div>
          )}

        {loading && (
          <div
            className="mt-auto h-5 flex flex-col justify-center"
            data-testid="search-results-loading"
          >
            <LinearProgress />
          </div>
        )}
      </div>
      <GlobalSearchControlsInfo />
    </div>
  );
};

export default GlobalSearch;
